import { NgModule } from "@angular/core";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { HttpClient } from "@angular/common/http";
import { MatMenuModule } from "@angular/material/menu";
import { MatIconModule } from "@angular/material/icon";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatDividerModule } from "@angular/material/divider";
import { MatButtonModule } from "@angular/material/button";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSortModule } from "@angular/material/sort";
import { MatPaginatorModule } from "@angular/material/paginator";
import { MatTableModule } from "@angular/material/table";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { LocalizedDatePipe } from "./localized-date.pipe";
import { FormatDurationPipe } from "./format-duration.pipe";
import { HttpLoaderFactory } from "../app.module";
import { MatRadioModule } from "@angular/material/radio";
import { MatDialogModule } from "@angular/material/dialog";
import { MatCardModule } from "@angular/material/card";
import { MatTabsModule } from "@angular/material/tabs";
import { LoadingComponent } from "./loading/loading.component";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { ImageUploaderComponent } from "./image-uploader/image-uploader.component";
import { CommonModule } from "@angular/common";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatTimepickerModule } from "@angular/material/timepicker";
import { MatSelectModule } from "@angular/material/select";
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatNativeDateModule,
} from "@angular/material/core";
import { MomentDateAdapter } from "@angular/material-moment-adapter";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ImageProcessingComponent } from "./image-processing/image-processing.component";
import { ImageCropperModule } from "ngx-image-cropper";
import { SafeHtmlPipe } from "./pipes/safe-html.pipe";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { ProgressButtonComponent } from "./progress-button/progress-button.component";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { OverlayModule } from "@angular/cdk/overlay";
import { ClientTableComponent } from "./client-table/client-table.component";
import { ClosablePanelComponent } from "./closable-panel/closable-panel.component";
import { AvatarUploaderComponent } from "./avatar-uploader/avatar-uploader.component";
import { AvatarComponent } from "./avatar/avatar.component";
import { AvatarUploaderModalComponent } from "./avatar-uploader-modal/avatar-uploader-modal.component";
import { HandwrittenSignatureComponent } from "./handwritten-signature/handwritten-signature.component";
import { BadgesComponent } from "./badges/badges.component";
import { BadgesDialogComponent } from "./badges/badges-dialog/badges-dialog.component";
// import {MatSelectFilterModule} from 'mat-select-filter';
import { TrialInfoComponent } from "./trial-info/trial-info.component";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { ReactiveFormsModule } from "@angular/forms";
import { FormsModule } from "@angular/forms";
import { TutorialModalComponent } from "./tutorial-modal/tutorial-modal.component";
import { GeminiChatComponent } from "./gemini-chat.component/gemini-chat.component";
import { ExtensionButtonComponent } from "./extension-button/extension-button.component";
import { GoogleReviewComponent } from "./google-review/google-review.component";
import { SignatureChoiceDialogComponent } from "./signature-choice-dialog/signature-choice-dialog.component";
import { InstallationChoiceDialogComponent } from "./installation-choice-dialog/installation-choice-dialog.component";
import { NameInputDialogComponent } from "./name-input-dialog/name-input-dialog.component";
import { TimePickerComponent } from "./time-picker/time-picker.component";
import { LimitPlanDialogComponent } from "./limit-plan-dialog/limit-plan-dialog.component";
import { SyncRequiredDialogComponent } from "./sync-required-dialog/sync-required-dialog.component";
import { ScaleContentDirective } from "./directives/scale-content.directive";
import { TableRowLinkDirective } from "./table/table-row-link.directive";
import { PhoneInputComponent } from "./phone-input/phone-input.component";
import { CountrySelectComponent } from "./country-select/country-select.component";
import { SearchableSelectComponent } from "./searchable-select/searchable-select.component";
import { NotifyAdminDialogComponent } from "./notify-admin-dialog/notify-admin-dialog.component";
import { EmployeeBranchSectionComponent } from "./employee-branch-section/employee-branch-section.component";
import { EmployeeVcardComponent } from "./employee-vcard/employee-vcard.component";
import { EditorComponent } from "./editor/editor.component";
import { RangeCalendarComponent } from "./range-calendar/range-calendar.component";
import { CalendarRangeHeaderComponent } from "./range-calendar/calendar-range-header/calendar-range-header.component";
import { CalendarRangePanelComponent } from "./range-calendar/calendar-range-panel/calendar-range-panel.component";
import { PrintColorComponent } from "./print-color/print-color.component";

export const MY_FORMATS = {
  parse: {
    dateInput: "LL",
    timeInput: "HH:mm",
  },
  display: {
    dateInput: "YYYY-MM-DD",
    monthYearLabel: "YYYY",
    dateA11yLabel: "LL",
    monthYearA11yLabel: "YYYY",
    time: "HH:mm:ss",
    timeInput: "HH:mm",
    timeOptionLabel: "HH:mm",
  },
};

const pipes = [LocalizedDatePipe, FormatDurationPipe, SafeHtmlPipe];

const components = [
  LoadingComponent,
  ImageUploaderComponent,
  ImageProcessingComponent,
  ProgressButtonComponent,
  ClientTableComponent,
  AvatarUploaderComponent,
  AvatarComponent,
  AvatarUploaderModalComponent,
  HandwrittenSignatureComponent,
  BadgesComponent,
  BadgesDialogComponent,
  TrialInfoComponent,
  TutorialModalComponent,
  GeminiChatComponent,
  ExtensionButtonComponent,
  GoogleReviewComponent,
  SignatureChoiceDialogComponent,
  InstallationChoiceDialogComponent,
  NameInputDialogComponent,
  TimePickerComponent,
  LimitPlanDialogComponent,
  SyncRequiredDialogComponent,
  PhoneInputComponent,
  CountrySelectComponent,
  SearchableSelectComponent,
  NotifyAdminDialogComponent,
  EmployeeBranchSectionComponent,
  EmployeeVcardComponent,
  EditorComponent,
  RangeCalendarComponent,
  CalendarRangeHeaderComponent,
  CalendarRangePanelComponent,
  PrintColorComponent,
];

const directives = [ScaleContentDirective, TableRowLinkDirective];

const materialModules = [
  MatMenuModule,
  MatIconModule,
  MatButtonModule,
  MatToolbarModule,
  MatSidenavModule,
  MatDividerModule,
  MatCheckboxModule,
  MatSortModule,
  MatPaginatorModule,
  MatTableModule,
  MatInputModule,
  MatFormFieldModule,
  MatRadioModule,
  MatDialogModule,
  MatCardModule,
  MatTabsModule,
  MatExpansionModule,
  MatDatepickerModule,
  MatTimepickerModule,
  MatSelectModule,
  MatNativeDateModule,
  MatAutocompleteModule,
  MatTooltipModule,
  ImageCropperModule,
  MatSlideToggleModule,
  MatSnackBarModule,
  MatProgressSpinnerModule,
  OverlayModule,
  // MatSelectFilterModule,
];

@NgModule({
  imports: [
    ...materialModules,
    AngularEditorModule,
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    TranslateModule.forChild({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
  ],
  declarations: [
    ...pipes,
    ...components,
    ClosablePanelComponent,
    ...directives,
  ],
  exports: [
    ...pipes,
    ...components,
    ...materialModules,
    TranslateModule,
    CommonModule,
    AngularEditorModule,
    ClosablePanelComponent,
    ...directives,
  ],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class SharedModule {}
